import type { PropsWithChildren } from 'react';
import { generatePath } from 'react-router-dom';
import { Button } from '@accure/ui';
import { ChevronRight, LoaderCircle } from 'lucide-react';

import { GetAllSites } from '@/api/fe-api/view/queries/get-all-sites';
import { Link } from '@/components/ui/link';
import type { Alert } from '@/entities/alert';
import { useCurrentAsset } from '@/hooks/use-current-asset';

type WarrantyDetailsLinkProps = {
  alert: Alert;
};

export function WarrantyDetailsLink({
  alert,
  children,
}: PropsWithChildren<WarrantyDetailsLinkProps>) {
  // NOTE: this is highly temporary solution, will need to be replaced!!

  /* ───────────── 1) Try asset from URL context ─────────── */
  const { asset: ctxAsset, isMobility } = useCurrentAsset();

  /* ───────────── 2) If missing, fetch & resolve ────────── */
  const sites = GetAllSites.useQuery();

  if (!ctxAsset && sites.isLoading) {
    return (
      <Button disabled>
        <LoaderCircle className="animate-spin" />
        Loading details link…
      </Button>
    );
  }

  const resolvedAsset =
    ctxAsset ||
    sites.data?.find((s) => alert.systemFrontendId === s.fullFrontendId);

  /* ───────────── 3) Build destination ────────── */
  /* ----------------- build destination + query ------------------- */
  let pathname: string;
  const params = new URLSearchParams();

  if (resolvedAsset) {
    const prefix = isMobility ? 'vehicle' : 'asset';

    pathname = generatePath(`/${prefix}/:siteId/warranty`, {
      siteId: resolvedAsset.id,
    });

    const unitId = alert.fullFrontendId;
    const parts = unitId.split('/'); // e.g. ["Smart_Hill", "B05", "Rack_03"]

    /*
     * ───────────── revised param logic ─────────────
     * 1 segment   → no query params (site level only)
     * 2 segments  → top = "site/top"
     * 3 segments  → top = "site/top",  unit = "site/top/unit"
     */
    if (parts.length >= 2) {
      params.set('top', parts.slice(0, 2).join('/')); // "Smart_Hill/B05"

      if (parts.length >= 3) {
        params.set('unit', unitId); // "Smart_Hill/B05/Rack_03"
      }
    }
  } else {
    pathname = '/warranty';
  }

  /* ───────────── 4) Render link/button ────────── */
  return (
    <Link
      to={{
        pathname,
        search: params.toString() || undefined,
        hash: alert.type,
      }}
    >
      {children ?? (
        <Button>
          Go to Warranty
          <ChevronRight />
        </Button>
      )}
    </Link>
  );
}
